import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { LOCAL_LAB_AUTH_TOKEN } from "@l30/shared";
import { setAuthTokenGetter as setAtlasAuthTokenGetter } from "@l30/web-atlas/api/client";
import { setAuthTokenGetter as setBoardAuthTokenGetter } from "@l30/web-board/api/client";
import { setAuthTokenGetter as setDocumentsAuthTokenGetter } from "@l30/web-documents/api/client";
import { setAuthTokenGetter as setProgrammeAuthTokenGetter } from "@l30/web-programme/api/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { setAuthTokenGetter } from "./api/client.js";
import { isLocalLabAuthMode } from "./lib/auth-mode.js";
import { initialiseBrowserTelemetry } from "./lib/browser-telemetry.js";
import { queryClient } from "./lib/query-client.js";
import { rootRoute } from "./routes/__root.js";
import { aiRoute } from "./routes/_authenticated/ai.js";
import { authenticatorRoute } from "./routes/_authenticated/authenticator.js";
import { boardMeetingsRoute } from "./routes/_authenticated/board-meetings.js";
import { cashflowRoute } from "./routes/_authenticated/cashflow.js";
import { companiesRoute } from "./routes/_authenticated/companies.js";
import { companyDetailRoute } from "./routes/_authenticated/company-detail.js";
import { contactDetailRoute } from "./routes/_authenticated/contact-detail.js";
import { contactGroupsRoute } from "./routes/_authenticated/contact-groups.js";
import { contactsRoute } from "./routes/_authenticated/contacts.js";
import { crmAttributionRoute } from "./routes/_authenticated/crm-attribution.js";
import { crmCampaignsRoute } from "./routes/_authenticated/crm-campaigns.js";
import { crmDealsRoute } from "./routes/_authenticated/crm-deals.js";
import { crmLeadsRoute } from "./routes/_authenticated/crm-leads.js";
import { dashboardRoute } from "./routes/_authenticated/dashboard.js";
import { dataRoomDetailRoute, dataRoomsRoute } from "./routes/_authenticated/data-rooms.js";
import { documentsRoute } from "./routes/_authenticated/documents.js";
import { emailRoute } from "./routes/_authenticated/email.js";
import { engagementPlanRoute } from "./routes/_authenticated/engagement-plan.js";
import { ideationChatRoute } from "./routes/_authenticated/ideation.chat.js";
import { ideationRoute } from "./routes/_authenticated/ideation.js";
import { ideationMapDetailRoute } from "./routes/_authenticated/ideation.maps.$mapId.js";
import { ideationMapsRoute } from "./routes/_authenticated/ideation.maps.js";
import { insightsHomeRoute } from "./routes/_authenticated/insights-home.js";
import { meetingsRoute } from "./routes/_authenticated/meetings.js";
import { messagesRoute } from "./routes/_authenticated/messages.js";
import { myVaultRoute } from "./routes/_authenticated/my-vault.js";
import { noteDetailRoute } from "./routes/_authenticated/note-detail.js";
import { notesRoute } from "./routes/_authenticated/notes.js";
import { partnerVaultsRoute } from "./routes/_authenticated/partner-vaults.js";
import { portalUsersRoute } from "./routes/_authenticated/portal-users.js";
import { portalUsersNewRoute } from "./routes/_authenticated/portal-users.new.js";
import { programmeCollaborationRoute } from "./routes/_authenticated/programme-collaboration.js";
import { projectDetailRoute } from "./routes/_authenticated/project-detail.js";
import { projectsRoute } from "./routes/_authenticated/projects.js";
import { receiptsRoute } from "./routes/_authenticated/receipts.js";
import { schedulingRoute } from "./routes/_authenticated/scheduling.js";
import { settingsRoute } from "./routes/_authenticated/settings.js";
import { settingsTokensRoute } from "./routes/_authenticated/settings.tokens.js";
import { signaturesRoute } from "./routes/_authenticated/signatures.js";
import { subjectRoute } from "./routes/_authenticated/subject.js";
import { tasksRoute } from "./routes/_authenticated/tasks.js";
import { timeTrackingRoute } from "./routes/_authenticated/time-tracking.js";
import { authenticatedRoute } from "./routes/_authenticated.js";
import { bookRoute } from "./routes/book.js";
import { cancelBookingRoute } from "./routes/cancel-booking.js";
import { rescheduleBookingRoute } from "./routes/reschedule-booking.js";
import { shareRoute } from "./routes/share.js";
import { signInRoute, signUpRoute } from "./routes/sign-in.js";
import { signRoute } from "./routes/sign.js";
import { statusRoute } from "./routes/status.js";
import "./styles/globals.css";
// Caveat font loads on the public sign route only (ADR-0101 Phase 2).

// Build route tree.
//
// Portal routes used to mount here too. They moved to the standalone
// `@l30/portal-web` SPA (PR — Day 10a). Customer-portal traffic is
// served by `swa-l30-portal-{env}` at `*.portal.latitude30.co`, the
// staff app at `app.latitude30.co` no longer carries portal code.
// Canon: docs/enhancements/portal-swa-split-2026-05.md.
const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  bookRoute,
  cancelBookingRoute,
  rescheduleBookingRoute,
  statusRoute,
  signRoute,
  shareRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    insightsHomeRoute,
    subjectRoute,
    projectsRoute,
    projectDetailRoute,
    tasksRoute,
    contactsRoute,
    contactDetailRoute,
    contactGroupsRoute,
    companiesRoute,
    companyDetailRoute,
    dataRoomsRoute,
    dataRoomDetailRoute,
    crmLeadsRoute,
    crmDealsRoute,
    crmCampaignsRoute,
    crmAttributionRoute,
    documentsRoute,
    myVaultRoute,
    boardMeetingsRoute,
    signaturesRoute,
    emailRoute,
    authenticatorRoute,
    cashflowRoute,
    receiptsRoute,
    meetingsRoute,
    messagesRoute,
    notesRoute,
    noteDetailRoute,
    aiRoute,
    ideationRoute,
    ideationMapsRoute,
    ideationMapDetailRoute,
    ideationChatRoute,
    partnerVaultsRoute,
    portalUsersRoute,
    portalUsersNewRoute,
    engagementPlanRoute,
    programmeCollaborationRoute,
    schedulingRoute,
    timeTrackingRoute,
    settingsRoute,
    settingsTokensRoute,
  ]),
]);

// Prefetch a route's code + loader on link-hover/focus (assessment U5)
// so lazy-route navigation feels instant instead of showing nothing
// until the chunk lands. `intent` = on hover/touchstart.
const router = createRouter({ routeTree, defaultPreload: "intent" });

// Register the router instance with TanStack Router's type system so route
// ids/paths are compile-time-checked across useNavigate/Link/navigate({to})
// repo-wide. Without this, a wrong route-path string (e.g. a stale template
// literal) fails only at RUNTIME — the class of bug that crashed every
// ideation-workspace smoke. With it, those become type errors at build.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Clerk publishable key
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey && !isLocalLabAuthMode) {
  console.warn("VITE_CLERK_PUBLISHABLE_KEY not set — auth will not work");
}

/**
 * Bridges Clerk's in-context getToken() to the module-level apiFetch client
 * so every API request sends an Authorization: Bearer <jwt> header.
 *
 * Set during render (not useEffect) so the getter is installed BEFORE child
 * useEffects run their first fetch. The previous useEffect placement
 * created a race: on a fresh page load, useQuery hooks would fire before
 * the bridge registered the token getter, every initial request returned
 * 401, and React Query happily showed empty/error state. Smoke caught
 * this on /projects/:id where two back-to-back navigations made the race
 * reproducible.
 *
 * setAuthTokenGetter is idempotent and cheap, so calling it on every
 * render is fine.
 */
function AuthTokenBridge({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const getter = async () => {
    try {
      return await getToken();
    } catch {
      return null;
    }
  };
  setAuthTokenGetter(getter);
  setAtlasAuthTokenGetter(getter);
  setDocumentsAuthTokenGetter(getter);
  setProgrammeAuthTokenGetter(getter);
  setBoardAuthTokenGetter(getter);
  return <>{children}</>;
}

const root = document.getElementById("root")!;

void initialiseBrowserTelemetry();

function AppProviders() {
  if (isLocalLabAuthMode) {
    setAuthTokenGetter(async () => LOCAL_LAB_AUTH_TOKEN);
    setAtlasAuthTokenGetter(async () => LOCAL_LAB_AUTH_TOKEN);
    setDocumentsAuthTokenGetter(async () => LOCAL_LAB_AUTH_TOKEN);
    setProgrammeAuthTokenGetter(async () => LOCAL_LAB_AUTH_TOKEN);
    setBoardAuthTokenGetter(async () => LOCAL_LAB_AUTH_TOKEN);
    return (
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    );
  }

  return (
    <ClerkProvider publishableKey={clerkPubKey ?? ""}>
      <AuthTokenBridge>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AuthTokenBridge>
    </ClerkProvider>
  );
}

createRoot(root).render(
  <StrictMode>
    <AppProviders />
  </StrictMode>,
);
